import type { KeyboardEvent } from "react"

/** Seconds moved by ArrowLeft and ArrowRight. */
const SMALL_STEP_SECONDS = 5
/** Seconds moved by PageUp and PageDown. */
const LARGE_STEP_SECONDS = 30

function plural(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? "" : "s"}`
}

/**
 * A position read the way a screen reader should speak it: "1 minute 5
 * seconds" rather than "01:05", which is announced as a ratio or a clock.
 * Seconds keep one decimal place, so a clip shorter than a second is not
 * announced as zero.
 */
function spokenAudioTime(totalSeconds: number): string {
  const tenths = Math.max(0, Math.round(totalSeconds * 10))
  const hours = Math.floor(tenths / 36000)
  const minutes = Math.floor((tenths % 36000) / 600)
  const seconds = (tenths % 600) / 10
  const parts = [
    hours > 0 ? plural(hours, "hour") : null,
    minutes > 0 ? plural(minutes, "minute") : null,
    seconds > 0 || tenths === 0 ? plural(seconds, "second") : null,
  ]
  return parts.filter(Boolean).join(" ")
}

/** Rounds a slider value to the tenth of a second it is announced at. */
function sliderValue(seconds: number): number {
  return Math.round(seconds * 10) / 10
}

/** The position a seek key moves to, or null for a key the slider ignores. */
function seekTargetForKey(
  key: string,
  position: number,
  duration: number,
): number | null {
  switch (key) {
    case "ArrowLeft":
    case "ArrowDown":
      return position - SMALL_STEP_SECONDS
    case "ArrowRight":
    case "ArrowUp":
      return position + SMALL_STEP_SECONDS
    case "PageDown":
      return position - LARGE_STEP_SECONDS
    case "PageUp":
      return position + LARGE_STEP_SECONDS
    case "Home":
      return 0
    case "End":
      return duration
    default:
      return null
  }
}

/**
 * The keyboard seek control for the waveform. It precedes the waveform's
 * segments in the tab order and covers the track so its focus ring outlines
 * it, but takes no pointer input: clicking and dragging still reach the
 * waveform underneath.
 */
export function AudioSeekSlider({
  currentTime,
  duration,
  getCurrentTime,
  onSeek,
  label = "Seek",
}: {
  currentTime: number
  duration: number
  /** The live playhead, which can be ahead of `currentTime` between renders. */
  getCurrentTime: () => number
  onSeek: (seconds: number) => void
  /** The slider's accessible name; name the recording when a page has several. */
  label?: string
}) {
  const position = Math.max(0, Math.min(duration, currentTime))

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = seekTargetForKey(event.key, getCurrentTime(), duration)
    if (target === null) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    onSeek(Math.max(0, Math.min(duration, target)))
  }

  return (
    <div className="coval-focus-inset pointer-events-none absolute inset-0 z-10">
      <div
        role="slider"
        tabIndex={0}
        aria-label={label}
        aria-orientation="horizontal"
        aria-valuemin={0}
        aria-valuemax={sliderValue(duration)}
        aria-valuenow={sliderValue(position)}
        aria-valuetext={`${spokenAudioTime(position)} of ${spokenAudioTime(duration)}`}
        className="absolute inset-0 rounded-md"
        onKeyDown={handleKeyDown}
      />
    </div>
  )
}
